import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import React, { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { isTouchDevice } from '../shared/mobile';

type TooltipProps = TooltipPrimitive.TooltipProviderProps & {
  children: React.ReactNode;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  align?: TooltipPrimitive.TooltipContentProps['align'];
  sideOffset?: number;
  className?: string;
  delayDuration?: number;
  text?: string;
  description?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  textClassName?: string;
  /** Duration in ms for the fade-in animation (default: 150) */
  animationDuration?: number;
  disabled?: boolean;
};

const slideOffsets: Record<string, { x: string; y: string }> = {
  top: { x: '0', y: '4px' },
  bottom: { x: '0', y: '-4px' },
  left: { x: '4px', y: '0' },
  right: { x: '-4px', y: '0' },
};

export function Tooltip(props: TooltipProps) {
  const {
    children,
    text,
    description,
    side = 'top',
    sideOffset = 5,
    delayDuration = 500,
    className = '',
    align = 'center',
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    textClassName = '',
    animationDuration = 150,
    disabled = false,
  } = props;

  const [internalOpen, setInternalOpen] = useState(false);
  const isTouchDeviceValue = isTouchDevice();

  if (disabled) {
    return <>{children}</>;
  }

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const onOpenChange = isControlled ? controlledOnOpenChange : setInternalOpen;

  const handleTriggerClick = function (e: React.MouseEvent) {
    if (isTouchDeviceValue) {
      e.preventDefault();
      onOpenChange?.(!open);
    }
  };

  const offset = slideOffsets[side] ?? slideOffsets.top;

  return (
    <TooltipPrimitive.Provider
      delayDuration={delayDuration}
      disableHoverableContent={isTouchDeviceValue}
    >
      <TooltipPrimitive.Root defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
        <TooltipPrimitive.Trigger asChild onClick={handleTriggerClick}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className={twMerge(
              'z-50 flex max-w-80 flex-col gap-0.5 rounded-lg border-none bg-branding-accent-high-contrast px-3 py-2 text-left shadow-tooltip',
              className
            )}
            sideOffset={sideOffset}
            side={side}
            align={align}
            data-radix-tooltip-content=""
            style={
              {
                '--tooltip-duration': `${animationDuration}ms`,
                '--tooltip-slide-x': offset.x,
                '--tooltip-slide-y': offset.y,
              } as React.CSSProperties
            }
            onPointerDownOutside={function (_e) {
              if (isTouchDeviceValue) {
                onOpenChange?.(false);
              }
            }}
          >
            <p className={twMerge('font-body-3-sb text-white', textClassName)}>{text}</p>
            {description != null ? <p className="font-body-3-r text-white">{description}</p> : null}
            <TooltipPrimitive.Arrow className="z-999 border-none fill-branding-accent-high-contrast" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
